import { ILogger, Logger, RemoteAudioPlayer, RemoteAudioTrack, RemoteVideoTrack, VideoPlayer } from '../..';
import { CHROME_MAJOR_VERSION, IS_ANDROID, IS_CHROMIUM_128_TO_143 } from '../../utils/environment';
import { PlayerEvent } from '../event';

interface AVPlayerStateSyncManagerOptions {
  log: Logger;
  audioPlayer: RemoteAudioPlayer;
  videoPlayer: VideoPlayer;
}

export default class AVPlayerStateSyncManager {
  audioPlayer: RemoteAudioPlayer;
  videoPlayer: VideoPlayer;
  log: ILogger;

  constructor(options: AVPlayerStateSyncManagerOptions) {
    this.audioPlayer = options.audioPlayer;
    this.videoPlayer = options.videoPlayer;

    this.log = options.log.createChild({ id: 'pip' });

    this.videoPlayer.on(PlayerEvent.USER_RESUME_IN_PIP_OR_FULL_SCREEN, this.handleUserResumeInPIPOrFullScreen, this);
    this.videoPlayer.on(PlayerEvent.USER_PAUSE_IN_PIP_OR_FULL_SCREEN, this.handleUserPauseInPIPOrFullScreen, this);

    this.videoPlayer.on(PlayerEvent.ENTER_PICTURE_IN_PICTURE, this.handleEnterPIPOrFullScreen, this);
    this.videoPlayer.on(PlayerEvent.ENTER_FULL_SCREEN, this.handleEnterPIPOrFullScreen, this);
    this.videoPlayer.on(PlayerEvent.LEAVE_PICTURE_IN_PICTURE, this.handleLeavePIP, this);
    this.videoPlayer.on(PlayerEvent.LEAVE_FULL_SCREEN, this.handleLeaveFullScreen, this);
    this.videoPlayer.on(PlayerEvent.VOLUME_CHANGE, this.handleVolumeChange, this);
  }

  handleUserResumeInPIPOrFullScreen() {
    if (this.audioPlayer.isPaused) {
      this.log.warn(`resume audio in ${this.videoPlayer.isPictureInPicture() ? 'pip' : 'fullscreen'}`);
      this.audioPlayer.doResume();
    }
    if (IS_ANDROID && IS_CHROMIUM_128_TO_143) {
      this.videoPlayer.resetSrcObjectToReplay();
    }
  }

  handleUserPauseInPIPOrFullScreen() {
    if (!this.audioPlayer.isPaused) {
      this.log.warn(`pause audio in ${this.videoPlayer.isPictureInPicture() ? 'pip' : 'fullscreen'}`);
      this.audioPlayer.doPause();
    }
  }

  handleEnterPIPOrFullScreen() {
    if (this.videoPlayer.element && this.audioPlayer.muted !== this.videoPlayer.element.muted) {
      this.log.warn(`sync video muted to ${this.audioPlayer.muted} when enter ${this.videoPlayer.isPictureInPicture() ? 'pip' : 'fullscreen'}`);
      this.videoPlayer.element.muted = this.audioPlayer.muted;
    }
  }

  handleLeavePIP() {
    if (this.audioPlayer.isPaused && !this.audioPlayer.isPausedByUserCall) {
      this.log.warn('resume after leave pip');
      this.audioPlayer.doResume();
    }
    if (this.videoPlayer.isPaused && !this.videoPlayer.isPausedByUserCall) {
      this.log.warn('resume video after leave pip');
      this.videoPlayer.doResume();
    }
  }

  handleLeaveFullScreen() {
    // 全屏时，video controls 会展示出来，如果用户在全屏暂停了，全屏退出时，需要主动恢复播放。避免退出全屏后无法恢复播放。
    if (this.audioPlayer.isPaused && !this.audioPlayer.isPausedByUserCall) {
      this.log.warn('resume audio after leave fullscreen');
      this.audioPlayer.doResume();
    }
    if (this.videoPlayer.isPaused && !this.videoPlayer.isPausedByUserCall) {
      this.log.warn('resume video after leave fullscreen');
      if (IS_ANDROID && IS_CHROMIUM_128_TO_143) {
        this.videoPlayer.resetSrcObjectToReplay();
      } else {
        this.videoPlayer.doResume();
      }
    }
  }

  handleVolumeChange(event: { muted: boolean | undefined }) {
    if (event.muted !== undefined && this.audioPlayer.muted !== event.muted) {
      this.log.warn(`sync audio muted to ${event.muted} in ${this.videoPlayer.isPictureInPicture() ? 'pip' : 'fullscreen'}`);
      this.audioPlayer.setMuted(event.muted);
    }
  }

  destroy() {
    this.videoPlayer.off(PlayerEvent.USER_RESUME_IN_PIP_OR_FULL_SCREEN, this.handleUserResumeInPIPOrFullScreen, this);
    this.videoPlayer.off(PlayerEvent.USER_PAUSE_IN_PIP_OR_FULL_SCREEN, this.handleUserPauseInPIPOrFullScreen, this);
    this.videoPlayer.off(PlayerEvent.ENTER_PICTURE_IN_PICTURE, this.handleEnterPIPOrFullScreen, this);
    this.videoPlayer.off(PlayerEvent.ENTER_FULL_SCREEN, this.handleEnterPIPOrFullScreen, this);
    this.videoPlayer.off(PlayerEvent.LEAVE_PICTURE_IN_PICTURE, this.handleLeavePIP, this);
    this.videoPlayer.off(PlayerEvent.LEAVE_FULL_SCREEN, this.handleLeaveFullScreen, this);
    this.videoPlayer.off(PlayerEvent.VOLUME_CHANGE, this.handleVolumeChange, this);
  }
}
